"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconChevronExpandYOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TOKEN_SERVICE_URL } from "@/consts/token-service";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
type TokenServiceAssetType = "perp" | "spot" | "tradexyz";
interface HyperliquidAsset {
  ticker: string;
  priceUsd: number | null;
  assetType: TokenServiceAssetType;
  name: string | null;
  szDecimals: number | null;
  maxLeverage: number | null;
}
interface HyperliquidPricesResponse {
  assets: HyperliquidAsset[];
}
export interface HyperliquidAssetAmountInputProps {
  coin: string;
  dex: string;
  size: string;
  onCoinChange: (coin: string) => void;
  onDexChange: (dex: string) => void;
  onSizeChange: (size: string) => void;
  side?: string;
  onSideChange?: (side: string) => void;
  orderType?: string;
  onOrderTypeChange?: (orderType: string) => void;
  price?: string;
  onPriceChange?: (price: string) => void;
  label?: string;
  placeholder?: string;
  popoverModal?: boolean;
}
const DEX_OPTIONS: {
  value: string;
  label: string;
  apiType: TokenServiceAssetType;
}[] = [{
  value: "",
  label: "Perps",
  apiType: "perp"
}, {
  value: "spot",
  label: "Spot",
  apiType: "spot"
}, {
  value: "xyz",
  label: "trade.xyz",
  apiType: "tradexyz"
}];
function dexToApiType(dex: string): TokenServiceAssetType {
  if (dex === "xyz" || dex === "flx" || dex === "vntl") return "tradexyz";
  if (dex === "spot") return "spot";
  return "perp";
}
function stripDexPrefix(name: string): string {
  const idx = name.indexOf(":");
  return idx !== -1 ? name.slice(idx + 1) : name;
}
function CoinLogo(t0) {
  const $ = _c(4);
  const {
    ticker,
    size
  } = t0;
  const name = stripDexPrefix(ticker).toLowerCase();
  const t1 = `https://ai-icon.b3.fun/logo/${name}`;
  const t2 = `${name} logo`;
  let t3;
  if ($[0] !== size || $[1] !== t1 || $[2] !== t2) {
    t3 = <img src={t1} alt={t2} width={size} height={size} className="rounded-full" loading="lazy" onError={_temp} />;
    $[0] = size;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
function formatPrice(price: number | null): string {
  if (price == null) return "-";
  if (price >= 1000) return `$${price.toLocaleString(undefined, {
    maximumFractionDigits: 2
  })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
}
async function fetchHyperliquidPrices(type: TokenServiceAssetType, signal?: AbortSignal): Promise<HyperliquidPricesResponse> {
  const res = await fetch(`${TOKEN_SERVICE_URL}/prices/hyperliquid?type=${type}`, {
    signal
  });
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
  return res.json();
}
function useHyperliquidAssets(dex) {
  const $ = _c(4);
  let t0;
  if ($[0] !== dex) {
    t0 = dexToApiType(dex);
    $[0] = dex;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const apiType = t0;
  let t1;
  if ($[2] !== apiType) {
    t1 = {
      queryKey: ["hyperliquid-prices", apiType],
      queryFn: t2 => {
        const {
          signal
        } = t2;
        return fetchHyperliquidPrices(apiType, signal);
      },
      staleTime: 30000,
      select: _temp2
    };
    $[2] = apiType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useQuery(t1);
}
function _temp2(data) {
  return data.assets;
}
const SIDE_OPTIONS = [{
  value: "buy",
  label: "Buy",
  activeClass: "border-green-500 bg-green-500/10 text-green-600 dark:text-green-400"
}, {
  value: "sell",
  label: "Sell",
  activeClass: "border-red-500 bg-red-500/10 text-red-600 dark:text-red-400"
}];
const ORDER_TYPE_OPTIONS = [{
  value: "limit",
  label: "Limit"
}, {
  value: "ioc",
  label: "IOC"
}, {
  value: "alo",
  label: "Post Only"
}];
const PRICE_PRESETS = [-5, -2, -1, 0, 1, 2, 5];
function LimitPriceInput(t0) {
  const $ = _c(15);
  const {
    price,
    onPriceChange,
    marketPrice
  } = t0;
  let t1;
  if ($[0] !== onPriceChange) {
    t1 = e => {
      const val = e.target.value;
      if (val && !/^[0-9]*\.?[0-9]*$/.test(val)) {
        return;
      }
      onPriceChange(val);
    };
    $[0] = onPriceChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleInput = t1;
  let t2;
  if ($[2] !== marketPrice || $[3] !== onPriceChange) {
    t2 = pct => {
      if (!marketPrice) {
        return;
      }
      if (pct === 0) {
        onPriceChange(marketPrice.toString());
        return;
      }
      const adjusted = marketPrice * (1 + pct / 100);
      const decimals = marketPrice >= 1000 ? 2 : marketPrice >= 1 ? 4 : 6;
      onPriceChange(adjusted.toFixed(decimals));
    };
    $[2] = marketPrice;
    $[3] = onPriceChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const applyPreset = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="label-xs-medium text-ds-text-muted shrink-0 pl-3">Limit Price</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== handleInput || $[7] !== price) {
    t4 = <div className="border-ds-border-default hover:border-ds-neutral-300 bg-ds-surface-card flex items-center rounded-xl border transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/30 dark:hover:border-zinc-600">{t3}<input type="text" inputMode="decimal" value={price} onChange={handleInput} placeholder="0.00" className="text-ds-text-primary dark:placeholder:text-ds-text-subtle min-w-0 flex-1 bg-transparent px-4 py-3 text-right text-lg font-semibold outline-none placeholder:text-zinc-300" /></div>;
    $[6] = handleInput;
    $[7] = price;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== applyPreset || $[10] !== marketPrice) {
    t5 = marketPrice != null && <div className="flex flex-wrap gap-1">{PRICE_PRESETS.map(pct_0 => <button key={pct_0} type="button" onClick={() => applyPreset(pct_0)} className={cn("rounded-md border px-2 py-1 text-xs font-medium transition-colors", pct_0 === 0 ? "border-blue-500/50 bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:text-blue-400" : "border-ds-border-default text-ds-text-muted hover:border-ds-neutral-300 dark:hover:border-zinc-600")}>{pct_0 === 0 ? "Market" : `${pct_0 > 0 ? "+" : ""}${pct_0}%`}</button>)}</div>;
    $[9] = applyPreset;
    $[10] = marketPrice;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="space-y-2">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export default React.memo(function HyperliquidAssetAmountInput(t0) {
  const $ = _c(86);
  const {
    coin,
    dex,
    size,
    onCoinChange,
    onDexChange,
    onSizeChange,
    side,
    onSideChange,
    orderType,
    onOrderTypeChange,
    price,
    onPriceChange,
    label,
    placeholder: t1,
    popoverModal: t2
  } = t0;
  const placeholder = t1 === undefined ? "0.00" : t1;
  const popoverModal = t2 === undefined ? false : t2;
  const [containerEl, setContainerEl] = React.useState(null);
  const [assetOpen, setAssetOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const {
    data: t3,
    isLoading,
    isError
  } = useHyperliquidAssets(dex);
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const assets = t4;
  let t5;
  let t6;
  if ($[2] !== coin) {
    t6 = stripDexPrefix(coin);
    $[2] = coin;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  t5 = t6;
  const normalizedCoin = t5;
  let t7;
  let t8;
  if ($[4] !== assets || $[5] !== coin || $[6] !== normalizedCoin) {
    t8 = assets.find(a => a.ticker === normalizedCoin || a.name === coin) ?? null;
    $[4] = assets;
    $[5] = coin;
    $[6] = normalizedCoin;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  t7 = t8;
  const selectedAsset = t7;
  let t9;
  bb0: {
    if (!searchQuery) {
      t9 = assets;
      break bb0;
    }
    let t10;
    if ($[8] !== assets || $[9] !== searchQuery) {
      const q = searchQuery.toLowerCase();
      t10 = assets.filter(a_0 => a_0.ticker.toLowerCase().includes(q) || (a_0.name?.toLowerCase().includes(q) ?? false));
      $[8] = assets;
      $[9] = searchQuery;
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    t9 = t10;
  }
  const filteredAssets = t9;
  const hasLimitPrice = onPriceChange != null;
  const prevCoinRef = React.useRef(coin);
  let t10;
  if ($[11] !== coin || $[12] !== hasLimitPrice || $[13] !== onPriceChange || $[14] !== price || $[15] !== selectedAsset) {
    t10 = () => {
      if (!hasLimitPrice || !onPriceChange || !selectedAsset?.priceUsd) {
        return;
      }
      const coinChanged = prevCoinRef.current !== coin;
      prevCoinRef.current = coin;
      if (!price || coinChanged) {
        onPriceChange(selectedAsset.priceUsd.toString());
      }
    };
    $[11] = coin;
    $[12] = hasLimitPrice;
    $[13] = onPriceChange;
    $[14] = price;
    $[15] = selectedAsset;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = selectedAsset?.priceUsd;
  let t12;
  if ($[17] !== coin || $[18] !== hasLimitPrice || $[19] !== t11) {
    t12 = [coin, t11, hasLimitPrice];
    $[17] = coin;
    $[18] = hasLimitPrice;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  React.useEffect(t10, t12);
  let t13;
  bb1: {
    const num = parseFloat(size);
    if (isNaN(num) || num <= 0) {
      t13 = null;
      break bb1;
    }
    if (hasLimitPrice && price) {
      const lp = parseFloat(price);
      if (!isNaN(lp) && lp > 0) {
        t13 = num * lp;
        break bb1;
      }
    }
    if (!selectedAsset?.priceUsd) {
      t13 = null;
      break bb1;
    }
    t13 = num * selectedAsset.priceUsd;
  }
  const usdEstimate = t13;
  let t14;
  if ($[21] !== onSizeChange) {
    t14 = e => {
      const val = e.target.value;
      if (val && !/^[0-9]*\.?[0-9]*$/.test(val)) {
        return;
      }
      onSizeChange(val);
    };
    $[21] = onSizeChange;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const handleSizeInput = t14;
  let t15;
  if ($[23] !== coin || $[24] !== onCoinChange || $[25] !== onDexChange) {
    t15 = newDex => {
      onDexChange(newDex);
      if (coin) {
        onCoinChange("");
      }
    };
    $[23] = coin;
    $[24] = onCoinChange;
    $[25] = onDexChange;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const handleDexChange = t15;
  let t16;
  if ($[27] !== label) {
    t16 = label && <label className="field-label mb-1.5 block">{label}</label>;
    $[27] = label;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== dex || $[30] !== handleDexChange) {
    t17 = DEX_OPTIONS.map(opt => <button key={opt.value} type="button" onClick={() => handleDexChange(opt.value)} className={cn("rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors", dex === opt.value ? "border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400" : "border-ds-border-default text-ds-text-muted hover:border-ds-neutral-300 dark:hover:border-zinc-600")}>{opt.label}</button>);
    $[29] = dex;
    $[30] = handleDexChange;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t17) {
    t18 = <div className="flex gap-1.5">{t17}</div>;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = nextOpen => {
      setAssetOpen(nextOpen);
      if (!nextOpen) {
        setSearchQuery("");
      }
    };
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== selectedAsset) {
    t20 = selectedAsset ? <><CoinLogo ticker={selectedAsset.ticker} size={20} /><span className="text-ds-text-primary">{selectedAsset.ticker}</span>{selectedAsset.priceUsd != null && <span className="caption-xs text-ds-text-muted ml-auto">{formatPrice(selectedAsset.priceUsd)}</span>}</> : <span className="text-ds-text-disabled">Select asset</span>;
    $[35] = selectedAsset;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <IconChevronExpandYOutline18 className="h-3.5 w-3.5 shrink-0 opacity-50" />;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t20) {
    t22 = <PopoverTrigger asChild={true}><button type="button" className="flex w-full items-center gap-2 px-3 py-3 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800/50">{t20}{t21}</button></PopoverTrigger>;
    $[38] = t20;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  const t23 = popoverModal ? containerEl ?? undefined : undefined;
  let t24;
  if ($[40] !== searchQuery) {
    t24 = <CommandInput placeholder="Search assets..." value={searchQuery} onValueChange={setSearchQuery} />;
    $[40] = searchQuery;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== filteredAssets || $[43] !== isError || $[44] !== isLoading || $[45] !== normalizedCoin || $[46] !== onCoinChange) {
    t25 = <CommandList className="max-h-72">{isLoading ? <div className="body-sm py-6 text-center">Loading assets...</div> : isError ? <div className="body-sm text-ds-text-muted py-6 text-center">Failed to load assets.</div> : filteredAssets.length === 0 ? <div className="body-sm text-ds-text-muted py-6 text-center">No asset found.</div> : <CommandGroup>{filteredAssets.map(asset => <CommandItem key={`${asset.assetType}-${asset.ticker}`} value={asset.ticker} onSelect={() => {
          onCoinChange(asset.ticker);
          setAssetOpen(false);
        }} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"><div className="flex w-full cursor-pointer items-center gap-2"><IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", normalizedCoin === asset.ticker ? "opacity-100" : "opacity-0")} /><CoinLogo ticker={asset.ticker} size={20} /><span className="flex-1 font-medium">{asset.ticker}</span><span className="caption-xs text-ds-text-muted">{formatPrice(asset.priceUsd)}</span>{asset.maxLeverage != null && <span className="caption-xs text-ds-text-disabled">{asset.maxLeverage}x</span>}</div></CommandItem>)}</CommandGroup>}</CommandList>;
    $[42] = filteredAssets;
    $[43] = isError;
    $[44] = isLoading;
    $[45] = normalizedCoin;
    $[46] = onCoinChange;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t24 || $[49] !== t25) {
    t26 = <Command shouldFilter={false}>{t24}{t25}</Command>;
    $[48] = t24;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t23 || $[52] !== t26) {
    t27 = <PopoverContent className="bg-ds-surface-card !z-[100] w-80 p-0" align="start" side="bottom" container={t23}>{t26}</PopoverContent>;
    $[51] = t23;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== assetOpen || $[55] !== popoverModal || $[56] !== t22 || $[57] !== t27) {
    t28 = <div className="border-ds-border-default hover:border-ds-neutral-300 bg-ds-surface-card flex items-center rounded-xl border transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/30 dark:hover:border-zinc-600"><Popover open={assetOpen} onOpenChange={t19} modal={popoverModal}>{t22}{t27}</Popover></div>;
    $[54] = assetOpen;
    $[55] = popoverModal;
    $[56] = t22;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== onSideChange || $[60] !== side) {
    t29 = onSideChange && <div className="space-y-1.5"><span className="field-label-muted">Side</span><div className="flex gap-1.5">{SIDE_OPTIONS.map(opt_0 => <button key={opt_0.value} type="button" onClick={() => onSideChange(opt_0.value)} className={cn("flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors", side === opt_0.value ? opt_0.activeClass : "border-ds-border-default text-ds-text-muted hover:border-ds-neutral-300 dark:hover:border-zinc-600")}>{opt_0.label}</button>)}</div></div>;
    $[59] = onSideChange;
    $[60] = side;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== onOrderTypeChange || $[63] !== orderType) {
    t30 = onOrderTypeChange && <div className="space-y-1.5"><span className="field-label-muted">Order Type</span><div className="flex gap-1.5">{ORDER_TYPE_OPTIONS.map(opt_1 => <button key={opt_1.value} type="button" onClick={() => onOrderTypeChange(opt_1.value)} className={cn("flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors", orderType === opt_1.value ? "border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400" : "border-ds-border-default text-ds-text-muted hover:border-ds-neutral-300 dark:hover:border-zinc-600")}>{opt_1.label}</button>)}</div></div>;
    $[62] = onOrderTypeChange;
    $[63] = orderType;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== hasLimitPrice || $[66] !== onPriceChange || $[67] !== price || $[68] !== selectedAsset?.priceUsd) {
    t31 = hasLimitPrice && <LimitPriceInput price={price ?? ""} onPriceChange={onPriceChange} marketPrice={selectedAsset?.priceUsd ?? null} />;
    $[65] = hasLimitPrice;
    $[66] = onPriceChange;
    $[67] = price;
    $[68] = selectedAsset?.priceUsd;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <span className="label-xs-medium text-ds-text-muted shrink-0 pl-3">Size</span>;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== handleSizeInput || $[72] !== placeholder || $[73] !== size) {
    t33 = <div className="border-ds-border-default hover:border-ds-neutral-300 bg-ds-surface-card flex items-center rounded-xl border transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/30 dark:hover:border-zinc-600">{t32}<input type="text" inputMode="decimal" value={size} onChange={handleSizeInput} placeholder={placeholder} className="text-ds-text-primary dark:placeholder:text-ds-text-subtle min-w-0 flex-1 bg-transparent px-4 py-3 text-right text-lg font-semibold outline-none placeholder:text-zinc-300" /></div>;
    $[71] = handleSizeInput;
    $[72] = placeholder;
    $[73] = size;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== usdEstimate) {
    t34 = usdEstimate != null && <div className="flex items-center justify-end px-1"><span className="caption-xs text-ds-text-disabled">Order value ≈ ${usdEstimate.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} USD</span></div>;
    $[75] = usdEstimate;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t16 || $[78] !== t18 || $[79] !== t28 || $[80] !== t29 || $[81] !== t30 || $[82] !== t31 || $[83] !== t33 || $[84] !== t34) {
    t35 = <div ref={setContainerEl} className="space-y-3">{t16}{t18}{t28}{t29}{t30}{t31}{t33}{t34}</div>;
    $[77] = t16;
    $[78] = t18;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
    $[83] = t33;
    $[84] = t34;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  return t35;
});